import React from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BaseDiv } from '../../common/BaseElements';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { Radio } from '../../components/Options/Radio/Radio';
import { CheckColour, CheckState } from '../../components/Options/Options';
import { ITooltipProps } from '../../components/Tooltip/TooltipBase';
import { generateOptionColours } from '../../fundamentals/colours/generateUiColours';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';

export interface IColourFlagSelectorProps extends Omit<ITooltipProps, 'children'> {
  colours?: CheckColour[];
  value?: CheckColour;
  defaultValue?: CheckColour;
  triggerOnClickOnly?: boolean;
  onChange?: (colour: CheckColour) => void;
  onToggleClick?: () => void;
}

export const Container = styled(BaseDiv)`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  & > * {
    display: flex;
  }
`;

export const TooltipBase = styled(Tooltip)``;

export const TooltipContainer = styled(BaseDiv)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${PX_TO_REM[8]};
`;

export const RadioBase = styled(Radio)`
  margin: 0;
`;

const ToggleContainer = styled(BaseDiv)<{ colour?: CheckColour }>`
  ${({ theme, colour }) => {
    const colours = generateOptionColours(theme.colours, undefined, colour);
    return css`
      cursor: pointer;
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      border-top: ${PX_TO_REM[12]} solid
        ${colour === undefined ? theme.colours.eoraCoreColours.BORDER_COLOR.COLOR_BORDER_DEFAULT : colours[CheckState.CHECKED].border};
      border-right: ${PX_TO_REM[12]} solid transparent;
    `;
  }}
`;

const Toggle = ({ selectedColour, onToggleClick, ...rest }: { selectedColour: CheckColour | undefined; onToggleClick?: () => void }) => {
  return <ToggleContainer colour={selectedColour} onClick={onToggleClick} {...rest} />;
};

const DEFAULT_COLOURS = [CheckColour.SUCCESS, CheckColour.INFO, CheckColour.WARNING, CheckColour.ERROR];

export const ColourFlagSelector: React.FC<IColourFlagSelectorProps> = ({ triggerOnClickOnly, colours, onChange, onToggleClick, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'ColourFlagSelector');
  const isControlled = rest.value !== undefined;
  const [internalColour, setInternalColour] = React.useState<CheckColour | undefined>(rest.defaultValue);

  const selectedColour = isControlled ? rest.value : internalColour;

  const handleColourChange = (colour: CheckColour) => {
    if (!isControlled) {
      setInternalColour(colour);
    }
    onChange?.(colour);
  };

  const colourOptions = colours ?? DEFAULT_COLOURS;

  return (
    <Container {...generateTestId()}>
      {triggerOnClickOnly ? (
        <Toggle selectedColour={selectedColour} onToggleClick={onToggleClick} {...generateTestId('Toggle')} />
      ) : (
        <TooltipBase
          {...generateTestId('Tooltip')}
          {...rest}
          text={
            <TooltipContainer>
              {colourOptions.map((colour) => (
                <RadioBase key={colour} colour={colour} checked={selectedColour === colour} onChange={() => handleColourChange(colour)} />
              ))}
            </TooltipContainer>
          }
        >
          <Toggle selectedColour={selectedColour} {...generateTestId('Toggle')} />
        </TooltipBase>
      )}
    </Container>
  );
};
